import { REWARDS_WIDGET_NAMES } from "@/constants/widget-names";
import {
    REWARDS_SPENDING_RULE_REDEEM_TYPES,
    REWARDS_SPENDING_RULE_TYPES,
} from "@apps/rewards/constants/rewardsSpendingRuleTypes";
import { rewardsContextToken } from "@apps/rewards/context_tokens";
import { RewardsSpendingRuleEntity } from "@apps/rewards/entities/entities";
import { rewardsWaysToSpendPageWidgetModalToken } from "@apps/rewards/tokens";
import { normalizeLocale } from "@apps/rewards/utils/normalizeLocale/normalizeLocale";
import { getTypedContext } from "@lib/svelte-typed-context";
import { combineLatest, lastValueFrom, map } from "rxjs";

export const rewardsWaysToSpendPageWidgetState = () => {
    //Context
    const {
        isCustomer$,
        rewardsSpendingRulesApiService,
        shopifyDiscountApiService,
        rewardsSpendingRulesQuery,
        rewardsProgramAccountQuery,
        pointsName$,
        rewardsCurrencyFormatter,
        rewardsAmountFormatter,
        localizationInfoProvider,
        rewardsModalManager,
    } = getTypedContext(rewardsContextToken);

    //Query
    const programAccount$ = rewardsProgramAccountQuery.programAccount$;
    const spendingRulesList$ = rewardsSpendingRulesQuery.spendingRulesList$;
    const status$ = rewardsSpendingRulesQuery.status$;

    //Variables
    const locale = normalizeLocale(
        localizationInfoProvider.getLanguageIsoCode()
    );

    //Logic
    const spendingRulesListViewData$ = combineLatest([
        spendingRulesList$,
        programAccount$,
    ]).pipe(
        map(([ruleList, programAccount]) =>
            ruleList.map((rule) => {
                let canRedeem = false;
                let missingPoints = 0;
                let pointsToSpend = 0;
                const userBalance = programAccount?.points?.balance || 0;

                if (
                    rule.type === REWARDS_SPENDING_RULE_TYPES.fixedAmount ||
                    rule.type === REWARDS_SPENDING_RULE_TYPES.percentage
                ) {
                    canRedeem = canRedeemPoints(
                        rule.pointsPrice,
                        userBalance,
                        rule.minSpendablePoints
                    );
                    missingPoints = getMissingPoints(
                        rule.pointsPrice,
                        userBalance,
                        rule.minSpendablePoints
                    );
                    pointsToSpend = rule.minSpendablePoints || rule.pointsPrice;
                } else {
                    canRedeem = canRedeemPoints(rule.pointsPrice, userBalance);
                    missingPoints = getMissingPoints(
                        rule.pointsPrice,
                        userBalance
                    );
                    pointsToSpend = rule.pointsPrice;
                }
                return {
                    ...rule,
                    canRedeem,
                    missingPoints: missingPoints,
                    pointsToSpend: pointsToSpend,
                    userBalance,
                };
            })
        )
    );

    const canRedeemPoints = (
        points: number,
        userBalance: number,
        minPoints?: number | null
    ) => {
        if (minPoints) return userBalance >= minPoints;

        return userBalance >= points;
    };

    const getMissingPoints = (
        points: number,
        userBalance: number,
        minPoints?: number | null
    ) => {
        if (minPoints) return minPoints - userBalance;

        return points - userBalance;
    };

    //Handlers
    const handleOpenModal = async (params: {
        rule: RewardsSpendingRuleEntity & { canRedeem: boolean };
        isCustomer: boolean;
    }): Promise<void> => {
        if (!params.isCustomer || !params.rule.canRedeem) return;

        await rewardsModalManager.openRewardsModal(
            REWARDS_WIDGET_NAMES.REWARDS_WAYS_TO_SPEND_PAGE_WIDGET_MODAL,
            {
                ruleId: params.rule.id,
            }
        );
    };

    const redeem = async (params: {
        ruleId: number;
        selectedVariantId?: number;
        spendPoints?: number;
    }): Promise<void> => {
        const { rewardCode, rewardType } = await lastValueFrom(
            rewardsSpendingRulesApiService.redeem(params)
        );

        if (
            rewardType === REWARDS_SPENDING_RULE_REDEEM_TYPES.orderDiscount ||
            rewardType === REWARDS_SPENDING_RULE_REDEEM_TYPES.shippingDiscount
        ) {
            void lastValueFrom(
                shopifyDiscountApiService.applyDiscount(
                    rewardCode,
                    params.ruleId
                )
            );
        }

        rewardsModalManager.closeModal(rewardsWaysToSpendPageWidgetModalToken);
    };

    const getSpendingRules = async () =>
        await lastValueFrom(
            rewardsSpendingRulesApiService.getSpendingRules({ locale })
        );

    const checkRequestStatusAndGetSpendingRules = async () => {
        await rewardsSpendingRulesApiService.checkRequestStatusAndGetSpendingRules(
            { locale }
        );
    };

    return {
        isCustomer$,
        spendingRulesListViewData$,
        status$,
        pointsName$,
        rewardsCurrencyFormatter,
        rewardsAmountFormatter,
        checkRequestStatusAndGetSpendingRules,
        handleOpenModal,
        redeem,
        getSpendingRules,
    };
};
